import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import TheoOrb from "./TheoOrb";
import TheoSidebar from "./TheoSidebar";
import {
  buildTheoContext,
  streamTheoChat,
  getOrCreateConversation,
  saveConversation,
  getDailyBrief,
  checkMemoryHealth,
  createNewConversation,
  loadConversation,
  deriveTitle,
  setConversationTitle,
} from "../lib/theoService";
import { THEO_SETUP_SQL, STEP_1_MIN, PHASE_4A_SQL } from "../lib/theoSetupSql";
import {
  shouldUseNativeStt,
  startNativeRecognition,
  recordToBackendStt,
  speakText,
  cancelTts,
  isNativeTtsSupported,
  isSpeaking,
} from "../lib/theoVoice";

const DISCLAIMER_KEY = "theo_disclaimer_acknowledged_v1";

const fmtDaysAgo = (n) => {
  if (n == null) return null;
  if (n === 0) return "today";
  if (n === 1) return "yesterday";
  return `${n}d ago`;
};

const confidenceMeta = (level) => {
  if (level === "high")   return { label: "High",   color: "#047857", bg: "rgba(16,185,129,0.10)", dot: "#10B981" };
  if (level === "medium") return { label: "Medium", color: "#92400E", bg: "rgba(245,158,11,0.10)", dot: "#F59E0B" };
  return                       { label: "Low",    color: "#6B7280", bg: "rgba(107,114,128,0.08)", dot: "#9CA3AF" };
};

// ── Personalized observation logic ───────────────────────────────
const buildObservation = (ctx) => {
  const has = {
    cycle: ctx.cycle_day != null,
    symptoms: ctx.last_symptom_update_days_ago != null && ctx.last_symptom_update_days_ago <= 14,
    hormones: ctx.last_hormone_upload_days_ago != null && ctx.last_hormone_upload_days_ago <= 120,
  };
  let headline, why;
  const phase = ctx.cycle_phase;
  const pattern = ctx.pcos_pattern || "";

  if (has.symptoms && phase === "luteal" && ctx.recent_symptoms?.some(s => s.toLowerCase().includes("fatigue"))) {
    headline = "Theo noticed your energy usually dips around this phase.";
    why = "Fatigue is trending up while you're in your luteal phase — a pattern many women with PCOS experience as progesterone shifts.";
  } else if (pattern.includes("insulin") && has.hormones) {
    headline = "Theo noticed insulin-related patterns in your recent data.";
    why = "This may be contributing to energy dips and cravings. Small choices today — protein-first meals, a short walk after eating — really add up.";
  } else if (pattern.includes("elevated LH/FSH")) {
    headline = "Theo noticed a classic LH/FSH pattern in your last report.";
    why = "Elevated LH relative to FSH is commonly seen with PCOS. A useful signal for where to focus next.";
  } else if (has.symptoms && (ctx.last_symptom_update_days_ago ?? 999) <= 1) {
    headline = "Theo noticed you've been consistent with tracking lately.";
    why = "Recent symptom data lets me spot patterns that would otherwise stay hidden. Thank you for sharing.";
  } else if (phase === "follicular") {
    headline = "Theo noticed you're entering your most energizing window.";
    why = "Follicular phase often brings clearer thinking and steadier mood. A good week to start something new.";
  } else if (phase === "menstrual") {
    headline = "Theo noticed your body is doing real work this week.";
    why = "Period days deserve rest, not productivity. Resting actually counts as progress right now.";
  } else if ((ctx.last_symptom_update_days_ago ?? 999) >= 5) {
    headline = `Theo hasn't seen a check-in for ${ctx.last_symptom_update_days_ago} days.`;
    why = "A quick log of how you're feeling helps me notice patterns and give you grounded guidance.";
  } else {
    headline = "Theo would like to learn more about you.";
    why = "Once I have your cycle, symptoms, and a recent hormone report, I can show you patterns that would otherwise be invisible.";
  }
  return { headline, why, has };
};

const buildTodaysFocus = (ctx) => {
  const items = [];
  if (ctx.recent_symptoms?.some((s) => s.toLowerCase().includes("fatigue"))) items.push("Protein-first breakfast");
  if (ctx.pcos_pattern?.includes("insulin"))                                  items.push("10-minute walk after meals");
  if (ctx.cycle_phase === "luteal")                                           items.push("Sleep before 11 PM");
  if ((ctx.last_symptom_update_days_ago ?? 999) >= 2)                         items.push("Track energy tonight");
  if (items.length === 0) {
    items.push("Drink water through the day");
    items.push("Move your body for 20 minutes");
  }
  return items.slice(0, 2);
};

const mostRecentUpdate = (ctx) => {
  const candidates = [ctx.last_symptom_update_days_ago, ctx.last_hormone_upload_days_ago, ctx.cycle_last_updated_days_ago].filter((x) => x != null);
  if (candidates.length === 0) return null;
  return fmtDaysAgo(Math.min(...candidates));
};

// Compute which transparency sources are available given current context.
// Used both for the hero card chips and for the per-response transparency chips.
const activeSources = (ctx) => {
  const list = [];
  if (ctx.cycle_day != null || ctx.cycle_phase) list.push("Cycle Data");
  if ((ctx.last_symptom_update_days_ago ?? 999) <= 14 || (ctx.recent_symptoms?.length || 0) > 0) list.push("Symptoms");
  if ((ctx.last_hormone_upload_days_ago ?? 999) <= 180 || ctx.pcos_pattern) list.push("Hormones");
  if ((ctx.last_hormone_upload_days_ago ?? 999) <= 180) list.push("Blood Report");
  if ((ctx.memories?.length || 0) > 0) list.push("Memories");
  return list;
};

// Build the Theo Memory Center — list of remembered facts as bullets
const buildTheoKnows = (ctx) => {
  const items = [];
  if (ctx.cycle_phase) items.push(`Currently in ${ctx.cycle_phase} phase (Day ${ctx.cycle_day ?? "?"})`);
  if (ctx.pcos_pattern) items.push(ctx.pcos_pattern.charAt(0).toUpperCase() + ctx.pcos_pattern.slice(1));
  if (ctx.hormone_values?.lh && ctx.hormone_values?.fsh) {
    const ratio = (ctx.hormone_values.lh / ctx.hormone_values.fsh).toFixed(1);
    items.push(`LH/FSH ratio of ${ratio} on your last report`);
  }
  if ((ctx.last_hormone_upload_days_ago ?? 999) <= 180) {
    items.push(`Recent blood report uploaded (${fmtDaysAgo(ctx.last_hormone_upload_days_ago)})`);
  }
  (ctx.recent_symptoms || []).slice(0, 2).forEach((s) => {
    items.push(s.charAt(0).toUpperCase() + s.slice(1));
  });
  (ctx.memories || []).slice(0, 3).forEach((m) => items.push(m));
  (ctx.goals || []).slice(0, 2).forEach((g) => items.push(`Goal: ${g}`));
  return items.length > 0 ? items.slice(0, 7) : ["Just getting to know you — share more as you go"];
};

// ── Smart starter chips below composer (data-aware) ──────────
// Returns 4-6 short prompts the user can tap to send. The set is selected
// based on what context Theo actually has — avoids irrelevant prompts.
const buildStarters = (ctx) => {
  const has = {
    cycle: ctx.cycle_day != null || ctx.cycle_phase,
    symptoms: (ctx.recent_symptoms?.length || 0) > 0
      || (ctx.last_symptom_update_days_ago ?? 999) <= 14,
    hormones: ctx.pcos_pattern
      || (ctx.last_hormone_upload_days_ago ?? 999) <= 180,
  };
  const fatigue = (ctx.recent_symptoms || []).some(s => /fatigue|tired/i.test(s));
  const memories = (ctx.memories?.length || 0) > 0;

  const all = [
    { id: "tired",      cond: fatigue || has.symptoms,         text: "Why am I tired today?" },
    { id: "hormones",   cond: has.hormones,                    text: "Review my hormones" },
    { id: "cycle",      cond: has.cycle,                       text: "Explain my cycle" },
    { id: "pattern",    cond: has.hormones || has.symptoms,    text: "What's my biggest pattern?" },
    { id: "focus",      cond: true,                            text: "What should I focus on this week?" },
    { id: "reports",    cond: has.hormones,                    text: "What stands out in my reports?" },
    { id: "remember",   cond: memories,                        text: "Remind me what we discussed" },
  ];
  // Pick the first 5 that match the user's data; "focus" is always shown.
  return all.filter(s => s.cond).slice(0, 5);
};

export default function TheoScreen({ userId, userName = "", onNavigate }) {
  const [acknowledged, setAcknowledged] = useState(
    () => typeof localStorage !== "undefined" && localStorage.getItem(DISCLAIMER_KEY) === "1"
  );
  const [context, setContext] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draftAssistant, setDraftAssistant] = useState("");
  const [sigilState, setSigilState] = useState("idle");
  const [input, setInput] = useState("");
  const [understandsOpen, setUnderstandsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [memHealth, setMemHealth] = useState(null);
  const [sqlCopied, setSqlCopied] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarNonce, setSidebarNonce] = useState(0);
  // Opens the "View Insights" bottom-sheet (only visible during Conversation Mode).
  const [insightsOpen, setInsightsOpen] = useState(false);
  // Voice MVP — when `voiceMode` is on, Theo's replies are spoken aloud.
  // `listening` tracks the active mic session (native or backend recorder).
  // `speaking` tracks whether TTS is currently producing audio, so the
  // speaker icon can show an "active" state and tap-again can cancel it.
  const [voiceMode, setVoiceMode] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState(""); // small badge text under composer
  const recognitionRef = useRef(null); // holds {stop, abort} or backend recorder
  const messagesEndRef = useRef(null);
  const heroRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!userId || !acknowledged) return;
    (async () => {
      try {
        const ctx = await buildTheoContext(userId, userName);
        if (cancelled) return;
        setContext(ctx);
        // Memory health probe — runs once on mount, surfaces UI warning if degraded
        checkMemoryHealth(userId).then(h => { if (!cancelled) setMemHealth(h); });
        const conv = await getOrCreateConversation(userId);
        if (cancelled) return;
        setConversation(conv);
        const existingMsgs = Array.isArray(conv?.messages) ? conv.messages : [];

        // Determine whether to show Theo's Daily Brief.
        // Rule: show it ONCE per calendar day. We mark a brief by adding a
        // `kind: "daily_brief"` field on the assistant message. If today's
        // brief is already in the conversation, skip; otherwise fetch.
        const today = new Date().toISOString().slice(0, 10);
        const hasTodaysBrief = existingMsgs.some(
          (m) => m.kind === "daily_brief" && (m.date || "").slice(0, 10) === today
        );
        setMessages(existingMsgs);
        if (!hasTodaysBrief) {
          const brief = await getDailyBrief(userId, ctx);
          if (!cancelled && brief) {
            const briefMsg = {
              role: "assistant",
              content: brief,
              kind: "daily_brief",
              date: new Date().toISOString(),
              sources: activeSources(ctx),
            };
            const withBrief = [...existingMsgs, briefMsg];
            setMessages(withBrief);
            // Persist so it's not regenerated on next visit
            saveConversation(conv?.id || null, withBrief, userId);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load Theo");
      }
    })();
    return () => { cancelled = true; };
  }, [userId, userName, acknowledged]);

  // Auto-scroll policy:
  //   • When the user sends a message (sending: false→true), scroll
  //     the orb into view ONCE — so the thinking animation is visible.
  //   • Subsequent streaming chunks (draftAssistant changing) do NOT
  //     re-trigger scroll — avoids fighting the user if they scroll
  //     manually mid-stream.
  //   • After the response finalizes (sending: true→false), gently
  //     scroll the latest message into view with block:"nearest" —
  //     only scrolls if needed, no aggressive jumps.
  //   • Works identically on mobile, tablet, and desktop.
  // Auto-scroll: the only behaviour the user cares about is that each
  // new turn lands the latest message in view, just above the fixed
  // composer dock. We add scroll-margin to messagesEndRef so the target
  // sits comfortably above the dock (~140px) and not behind it.
  useEffect(() => {
    if (sending) return;
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages.length, sending, draftAssistant]);

  const acknowledgeDisclaimer = () => {
    localStorage.setItem(DISCLAIMER_KEY, "1");
    setAcknowledged(true);
  };

  const sendMessage = useCallback(async (text) => {
    if (!text || !text.trim() || !userId) return;
    // Stop any in-flight speech immediately — a new response will arrive,
    // we should not let the previous one keep speaking.
    cancelTts();
    setSpeaking(false);
    setError(""); setInput(""); setSending(true); setSigilState("thinking");
    const userMsg = { role: "user", content: text.trim() };
    const updatedMsgs = [...messages, userMsg];
    setMessages(updatedMsgs);
    setDraftAssistant("");
    // Persist user message immediately so even if Theo's response fails, we don't lose it
    saveConversation(conversation?.id || null, updatedMsgs, userId);

    try {
      const ctx = await buildTheoContext(userId, userName);
      setContext(ctx);
      const sources = activeSources(ctx);
      const sessionId = conversation?.id || `${userId}-${Date.now()}`;
      let firstChunk = true;
      const final = await streamTheoChat({
        userId, sessionId, userMessage: text.trim(),
        history: messages, context: ctx,
        onChunk: (_delta, full) => {
          if (firstChunk) { setSigilState("responding"); firstChunk = false; }
          setDraftAssistant(full.split("<theo_state_update>")[0]);
        },
      });
      const finalMsgs = [...updatedMsgs, { role: "assistant", content: final, sources }];
      setMessages(finalMsgs);
      setDraftAssistant("");
      setSigilState("celebrating");
      setTimeout(() => setSigilState("idle"), 1100);
      saveConversation(conversation?.id || null, finalMsgs, userId);

      // Voice mode: auto-speak the final reply. cancelTts() inside speakText
      // ensures any in-flight speech is stopped before the new one begins.
      if (voiceMode) {
        const clean = (final || "").replace(/<theo_state_update>[\s\S]*?<\/theo_state_update>/g, "").trim();
        if (clean) {
          setSpeaking(true);
          speakText(clean).finally(() => {
            if (!isSpeaking()) setSpeaking(false);
          });
        }
      }

      // Auto-derive title from first user message after the first round-trip
      if (conversation?.id) {
        const isDefault = !conversation.title || /^(new conversation|conversation with theo)$/i.test(conversation.title);
        const firstUser = finalMsgs.find(m => m.role === "user")?.content;
        if (isDefault && firstUser) {
          const t = deriveTitle(firstUser);
          setConversation(prev => prev ? { ...prev, title: t } : prev);
          setConversationTitle(conversation.id, t);
          setSidebarNonce(n => n + 1); // refresh sidebar list with new title
        }
      }
    } catch (e) {
      setError(e.message || "Theo couldn't respond. Please try again.");
      setSigilState("idle"); setDraftAssistant("");
    }
    setSending(false);
  }, [userId, userName, messages, conversation, voiceMode]);

  // ── Voice MVP — tap-to-talk handler ────────────────────────────────────
  // Stops any in-progress TTS, then opens a mic session. Native Web Speech
  // first; falls back to MediaRecorder → backend Whisper.
  const startVoiceCapture = useCallback(async () => {
    if (sending || listening) return;
    cancelTts();
    setVoiceStatus("Listening… tap again to stop");
    setListening(true);

    if (shouldUseNativeStt()) {
      const handle = startNativeRecognition({
        onInterim: (t) => setInput(t),
        onFinal: (final) => {
          setListening(false);
          setVoiceStatus("");
          recognitionRef.current = null;
          const text = (final || "").trim();
          if (text) {
            setInput("");
            // ensure voice mode is on so the reply is spoken back
            setVoiceMode(true);
            sendMessage(text);
          }
        },
        onError: (err) => {
          setListening(false);
          recognitionRef.current = null;
          if (err === "no-speech") setVoiceStatus("Didn't catch that — tap mic and try again.");
          else if (err === "not-allowed" || err === "service-not-allowed") setVoiceStatus("Mic permission denied.");
          else setVoiceStatus("Voice unavailable, falling back…");
          // Auto-fall through to backend recorder on any error other than no-speech
          if (err !== "no-speech") {
            setTimeout(() => startVoiceCapture(), 200);
          }
        },
      });
      if (handle) recognitionRef.current = { kind: "native", ...handle };
      else {
        setListening(false);
        setVoiceStatus("Couldn't start microphone.");
      }
    } else {
      // Backend fallback recorder
      const rec = await recordToBackendStt({
        onError: (e) => setVoiceStatus(
          e === "mic_permission_denied" ? "Mic permission denied." :
          e === "no_mic_api" ? "Microphone not supported in this browser." :
          "Transcription failed — please type instead."
        ),
      });
      recognitionRef.current = { kind: "backend", ...rec };
      // The user taps mic again (or the dedicated stop) to finalize.
    }
  }, [sending, listening, sendMessage]);

  const stopVoiceCapture = useCallback(async () => {
    const r = recognitionRef.current;
    if (!r) { setListening(false); setVoiceStatus(""); return; }
    if (r.kind === "native") {
      r.stop?.();
      // onFinal will clear state
    } else {
      setVoiceStatus("Transcribing…");
      const text = await r.stop();
      setListening(false);
      setVoiceStatus("");
      recognitionRef.current = null;
      if ((text || "").trim()) {
        setInput("");
        setVoiceMode(true);
        sendMessage(text.trim());
      }
    }
  }, [sendMessage]);

  // Stop any TTS / recognition when the user navigates away from Theo
  useEffect(() => () => {
    cancelTts();
    const r = recognitionRef.current;
    if (r) { try { r.abort?.() ?? r.stop?.(); } catch (_) { /* ignore */ } }
  }, []);

  const ctx = context || {};
  const conf = confidenceMeta(ctx.confidence || "low");
  const obs = useMemo(() => buildObservation(ctx), [ctx]);
  const focus = useMemo(() => buildTodaysFocus(ctx), [ctx]);
  const updated = mostRecentUpdate(ctx);
  const isChatActive = messages.length > 0 || sending || draftAssistant;
  const hasAnyData = obs.has.cycle || obs.has.symptoms || obs.has.hormones;

  // Orb state is derived from voice + sending flags — priority order:
  //   listening > speaking > thinking > celebrating > idle.
  // Each maps to a distinct CSS class on <TheoOrb> for visual feedback.
  const orbState = listening ? "listening"
                 : speaking ? "speaking"
                 : sending ? "thinking"
                 : (draftAssistant ? "responding" : sigilState);

  // ── Disclaimer ─────────────────────────────────
  if (!acknowledged) {
    return (
      <div data-testid="theo-disclaimer" style={{ padding: 28, paddingBottom: 110, background: "#FAFAF9", minHeight: "100vh" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <TheoOrb state="idle" size="xl" />
        </div>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#0F172A", margin: "0 0 6px", textAlign: "center", letterSpacing: "-0.018em" }}>
          Meet Theo
        </h2>
        <p style={{ fontSize: 14, color: "#6B7280", textAlign: "center", marginBottom: 28, lineHeight: 1.55 }}>
          A warm, intelligent companion for your PCOS journey.
        </p>
        <div style={{
          background: "#fff", border: "1px solid #F1F5F9",
          borderRadius: 18, padding: 22, marginBottom: 22,
          boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, color: "#7C3AED", marginBottom: 8, letterSpacing: "0.14em" }}>
            BEFORE WE BEGIN
          </div>
          <p style={{ fontSize: 13.5, color: "#334155", margin: 0, lineHeight: 1.65 }}>
            Theo provides educational information and wellness support based on your data.
            <strong> Theo is not a doctor</strong> and doesn&apos;t diagnose, prescribe, or
            replace professional medical advice. For anything urgent, please reach out to
            a healthcare provider.
          </p>
        </div>
        <button onClick={acknowledgeDisclaimer} data-testid="theo-disclaimer-accept-btn" className="btn-primary" style={{ width: "100%" }}>
          I understand &mdash; let&apos;s begin
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 18,
        // Top padding is bigger during Conversation Mode to clear the fixed
        // Theo header (orb + name + insights button ≈ 180px tall) plus the
        // global app-header (~60px sticky bar above us).
        paddingTop: isChatActive ? 250 : 18,
        // Reserve space at the bottom for the fixed composer dock (composer
        // 52 + chips 44 + voice status 18 + breathing room 60 ≈ 174) PLUS
        // the bottom-nav's 64px height. Sum ≈ 238, round to 240.
        paddingBottom: 240,
        background: "#FAFAF9",
        minHeight: "100vh",
      }}
      data-testid="theo-screen"
    >

      {/* ── FIXED THEO HEADER (Conversation Mode only) ─────────────────────
          Keeps the orb visible & alive while the conversation grows.
          ~178px tall total → 18-19% of a 932px viewport.
          `top: 60px` clears the global sticky <header class="app-header"> which
          sits above us at z-index 50. */}
      {isChatActive && (
        <header
          data-testid="theo-fixed-header"
          style={{
            position: "fixed",
            top: 60,
            left: "50%",
            transform: "translateX(-50%)",
            width: "100%",
            // Matches the inner content column (.app-shell 420 - .app-content's
            // 20px horizontal padding × 2 = 380). Keeps the blurred backdrop
            // perfectly aligned with the message bubbles + composer beneath it.
            maxWidth: 380,
            padding: "10px 18px 12px",
            background: "linear-gradient(180deg, #FAFAF9 0%, rgba(250,250,249,0.96) 70%, rgba(250,250,249,0) 100%)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            // Prevents the backdrop-filter blur from leaking past the
            // container edges on iPad/tablet viewports.
            overflow: "hidden",
            zIndex: 40,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Inline mini-topbar (sidebar toggle + new chat) */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", marginBottom: 4 }}>
            <button
              data-testid="theo-sidebar-toggle"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open conversations"
              style={{
                background: "#fff", border: "1px solid #F1F5F9", borderRadius: 12,
                width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="15" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <button
              data-testid="theo-topbar-new-chat"
              onClick={async () => {
                const conv = await createNewConversation(userId);
                if (conv) {
                  setConversation(conv);
                  setMessages([]);
                  setSidebarNonce(n => n + 1);
                }
              }}
              style={{
                background: "transparent", border: "1px solid #DDD6FE",
                color: "#6D28D9", borderRadius: 10, padding: "5px 11px",
                fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 4,
              }}
            >
              <span style={{ fontSize: 13, lineHeight: 1 }}>+</span> New
            </button>
          </div>

          {/* The living orb — always animating, always present */}
          <div style={{ marginTop: 2 }}>
            <TheoOrb state={orbState} size="header" />
          </div>

          {/* Compact name + insights launcher */}
          <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: "-0.012em" }}>Theo</span>
            <span style={{ width: 3, height: 3, borderRadius: "50%", background: "#CBD5E1" }} />
            <button
              data-testid="theo-view-insights-btn"
              onClick={() => setInsightsOpen(true)}
              style={{
                background: "transparent",
                color: "#7C3AED",
                border: "none",
                padding: 0,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                letterSpacing: "-0.005em",
              }}
              aria-label="View today's insights"
            >
              View insights
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
        </header>
      )}

      {/* ── TOP BAR (pre-Conversation Mode only): sidebar + new-chat ─────── */}
      {!isChatActive && (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <button
          data-testid="theo-sidebar-toggle"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open conversations"
          style={{
            background: "#fff", border: "1px solid #F1F5F9", borderRadius: 12,
            width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="15" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <button
          data-testid="theo-topbar-new-chat"
          onClick={async () => {
            const conv = await createNewConversation(userId);
            if (conv) {
              setConversation(conv);
              setMessages([]);
              setSidebarNonce(n => n + 1);
            }
          }}
          style={{
            background: "transparent", border: "1px solid #DDD6FE",
            color: "#6D28D9", borderRadius: 12, padding: "8px 14px",
            fontSize: 12.5, fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>+</span> New Chat
        </button>
      </div>
      )}

      <TheoSidebar
        userId={userId}
        open={sidebarOpen}
        activeConversationId={conversation?.id || null}
        refreshNonce={sidebarNonce}
        onClose={() => setSidebarOpen(false)}
        onNewChat={async () => {
          const conv = await createNewConversation(userId);
          if (conv) {
            setConversation(conv);
            setMessages([]);
            setSidebarOpen(false);
            setSidebarNonce(n => n + 1);
          }
        }}
        onSelect={async (item) => {
          const conv = await loadConversation(item.id);
          if (conv) {
            setConversation(conv);
            setMessages(Array.isArray(conv.messages) ? conv.messages : []);
            setSidebarOpen(false);
          }
        }}
      />

      {/* ── Memory persistence health banner ───────────────────────────
         Only visible when one or more Theo tables are missing in Supabase.
         One-tap "Copy SQL" puts the setup script onto the clipboard so the
         user can paste straight into Supabase SQL Editor. */}
      {memHealth && !memHealth.ok && memHealth.missing.length > 0 && (
        <div
          data-testid="theo-memory-warning"
          style={{
            background: "linear-gradient(180deg, rgba(253, 230, 138, 0.20), rgba(252, 211, 167, 0.18))",
            border: "1px solid rgba(251, 191, 36, 0.30)",
            borderRadius: 14,
            padding: "12px 14px",
            marginBottom: 14,
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ fontSize: 14, lineHeight: 1.4 }}>⚠️</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#78350F", marginBottom: 2 }}>
                Theo&apos;s long-term memory is offline
              </div>
              <div style={{ fontSize: 11.5, color: "#92400E", lineHeight: 1.5 }}>
                Conversations work in this session only. Tap below to copy the setup SQL,
                then paste it into your Supabase SQL Editor.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button
              data-testid="theo-copy-sql-btn"
              onClick={async () => {
                const sql = THEO_SETUP_SQL;
                try {
                  await navigator.clipboard.writeText(sql);
                } catch (e) {
                  const ta = document.createElement("textarea");
                  ta.value = sql; document.body.appendChild(ta);
                  ta.select(); try { document.execCommand("copy"); } catch (_) {}
                  document.body.removeChild(ta);
                }
                setSqlCopied("full");
                setTimeout(() => setSqlCopied(false), 2400);
              }}
              style={{
                background: sqlCopied === "full" ? "#059669" : "#7C3AED",
                color: "#fff", border: "none", borderRadius: 999,
                padding: "7px 16px", fontSize: 12, fontWeight: 600,
                cursor: "pointer", letterSpacing: "-0.005em",
                transition: "background 0.2s ease",
              }}
            >
              {sqlCopied === "full" ? "✓ Copied — paste into Supabase" : "Copy full setup SQL"}
            </button>
            <button
              data-testid="theo-copy-sql-min-btn"
              onClick={async () => {
                const sql = STEP_1_MIN;
                try { await navigator.clipboard.writeText(sql); }
                catch (e) {
                  const ta = document.createElement("textarea");
                  ta.value = sql; document.body.appendChild(ta);
                  ta.select(); try { document.execCommand("copy"); } catch (_) {}
                  document.body.removeChild(ta);
                }
                setSqlCopied("min");
                setTimeout(() => setSqlCopied(false), 2400);
              }}
              style={{
                background: sqlCopied === "min" ? "#059669" : "transparent",
                color: sqlCopied === "min" ? "#fff" : "#78350F",
                border: "1px solid rgba(251, 191, 36, 0.40)",
                borderRadius: 999,
                padding: "7px 14px", fontSize: 12, fontWeight: 600,
                cursor: "pointer", transition: "all 0.2s ease",
              }}
            >
              {sqlCopied === "min" ? "✓ Copied — try this first" : "Copy minimal (1 table)"}
            </button>
            <button
              data-testid="theo-recheck-btn"
              onClick={async () => {
                const h = await checkMemoryHealth(userId);
                setMemHealth(h);
              }}
              style={{
                background: "transparent",
                color: "#78350F",
                border: "1px solid rgba(251, 191, 36, 0.40)",
                borderRadius: 999,
                padding: "7px 14px", fontSize: 12, fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Re-check
            </button>
          </div>
          {memHealth && memHealth.missing.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 10.5, color: "#92400E", letterSpacing: "0.02em" }}>
              Missing tables: {memHealth.missing.join(", ")}
            </div>
          )}
        </div>
      )}

      {/* ── Phase 4A migration banner — shown when archive/notification schema is missing ── */}
      {memHealth && memHealth.ok && memHealth.phase4aReady === false && (
        <div
          data-testid="theo-phase4a-warning"
          style={{
            background: "linear-gradient(180deg, rgba(221, 214, 254, 0.30), rgba(196, 181, 253, 0.22))",
            border: "1px solid rgba(167, 139, 250, 0.35)",
            borderRadius: 14,
            padding: "12px 14px",
            marginBottom: 14,
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ fontSize: 14, lineHeight: 1.4 }}>✨</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#5B21B6", marginBottom: 2 }}>
                Enable archive &amp; daily notifications
              </div>
              <div style={{ fontSize: 11.5, color: "#6D28D9", lineHeight: 1.5 }}>
                Theo&apos;s core memory works. One more small SQL adds archive support and
                daily push preferences. Paste into Supabase SQL Editor.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button
              data-testid="theo-copy-phase4a-btn"
              onClick={async () => {
                try { await navigator.clipboard.writeText(PHASE_4A_SQL); }
                catch (_) {
                  const ta = document.createElement("textarea");
                  ta.value = PHASE_4A_SQL; document.body.appendChild(ta);
                  ta.select(); try { document.execCommand("copy"); } catch (_) { /* ignore */ }
                  document.body.removeChild(ta);
                }
                setSqlCopied("p4a");
                setTimeout(() => setSqlCopied(false), 2400);
              }}
              style={{
                background: sqlCopied === "p4a" ? "#059669" : "#7C3AED",
                color: "#fff", border: "none", borderRadius: 999,
                padding: "7px 16px", fontSize: 12, fontWeight: 600,
                cursor: "pointer", letterSpacing: "-0.005em",
                transition: "background 0.2s ease",
              }}
            >
              {sqlCopied === "p4a" ? "✓ Copied — paste into Supabase" : "Copy Phase 4A SQL"}
            </button>
            <button
              data-testid="theo-phase4a-recheck-btn"
              onClick={async () => {
                const h = await checkMemoryHealth(userId);
                setMemHealth(h);
              }}
              style={{
                background: "transparent",
                color: "#5B21B6",
                border: "1px solid rgba(167, 139, 250, 0.35)",
                borderRadius: 999,
                padding: "7px 14px", fontSize: 12, fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Re-check
            </button>
          </div>
        </div>
      )}

      {/* ── HERO: Sigil + name + Today's Observation as one unified card ── */}
      {!isChatActive && (
      <div ref={heroRef} className="theo-fade" style={{ marginBottom: 16, scrollMarginTop: 16 }}>
        <div style={{
          background: "#fff",
          borderRadius: 24,
          padding: "26px 22px 22px",
          boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 12px 32px rgba(15,23,42,0.05)",
          border: "1px solid #F1F5F9",
          position: "relative",
          overflow: "hidden",
        }}>
          {/* Sigil + identity */}
          <div style={{ textAlign: "center", marginBottom: 18 }}>
            <TheoOrb state={orbState} size="lg" />
            <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: "12px 0 2px", letterSpacing: "-0.018em" }}>
              Theo
            </h2>
            <p style={{ fontSize: 12.5, color: "#64748B", margin: 0 }}>
              Your PCOS companion{updated ? ` · updated ${updated}` : ""}
            </p>
          </div>

          {/* Observation */}
          <div data-testid="theo-insight-of-day" style={{
            borderTop: "1px solid #F1F5F9",
            paddingTop: 18,
          }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: "#7C3AED", letterSpacing: "0.14em", marginBottom: 8 }}>
              TODAY
            </div>
            <p style={{ fontSize: 16.5, color: "#0F172A", margin: "0 0 8px", lineHeight: 1.45, fontWeight: 600, letterSpacing: "-0.008em" }}>
              {obs.headline}
            </p>
            <p style={{ fontSize: 13.5, color: "#475569", margin: "0 0 14px", lineHeight: 1.6 }}>
              {obs.why}
            </p>

            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 12, flexWrap: "wrap",
            }}>
              <div data-testid="theo-data-sources" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <SourcePill label="Cycle" ok={obs.has.cycle} />
                <SourcePill label="Hormones" ok={obs.has.hormones} />
                <SourcePill label="Symptoms" ok={obs.has.symptoms} />
              </div>
              <span data-testid="theo-confidence-badge" style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                fontSize: 10.5, fontWeight: 700, color: conf.color, padding: "3px 9px",
                background: conf.bg, borderRadius: 999, letterSpacing: "0.04em",
              }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: conf.dot }} />
                {conf.label.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* ── COMPOSER DOCK — fixed at bottom, above the bottom-nav ──────
          Stays visible regardless of how long the conversation grows.
          Width (380) matches the inner content column exactly so the
          dock's left/right edges align flush with the fixed Theo header,
          chat thread, and dashboard cards above it. */}
      <div
        data-testid="theo-composer-dock"
        style={{
          position: "fixed",
          // BottomNav is 64px tall and sits at bottom:0 — anchor above it.
          bottom: 64,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          // 380 = .app-shell (420) − .app-content's horizontal padding (20×2).
          // Matches the fixed header and the Theo content column exactly.
          maxWidth: 380,
          padding: "10px 18px 8px",
          background: "linear-gradient(180deg, rgba(250,250,249,0) 0%, rgba(250,250,249,0.92) 22%, #FAFAF9 60%)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          // Clips the backdrop-filter blur so it never bleeds past the
          // container edges on iPad / tablet viewports.
          overflow: "hidden",
          // Above conversation content (which has no explicit z-index), below
          // the sidebar scrim (z-index 999).
          zIndex: 50,
          boxSizing: "border-box",
        }}
      >
        <Composer
          input={input}
          setInput={setInput}
          onSend={sendMessage}
          sending={sending}
          hasData={hasAnyData}
          listening={listening}
          voiceMode={voiceMode}
          speaking={speaking}
          onVoiceToggle={() => {
            if (listening) stopVoiceCapture();
            else startVoiceCapture();
          }}
          onVoiceModeToggle={() => {
            // Tap-to-play / tap-to-stop semantics:
            //  • If we're currently speaking OR voice mode is ON → cancel + turn off
            //  • Else → turn on AND immediately speak the last assistant reply
            //    (so a tap on the speaker icon "plays" what Theo just said)
            if (speaking || voiceMode) {
              cancelTts();
              setSpeaking(false);
              setVoiceMode(false);
              return;
            }
            setVoiceMode(true);
            const lastAssistant = [...messages].reverse().find(m => m.role === "assistant");
            if (lastAssistant?.content) {
              const clean = lastAssistant.content
                .replace(/<theo_state_update>[\s\S]*?<\/theo_state_update>/g, "")
                .trim();
              if (clean) {
                setSpeaking(true);
                speakText(clean).finally(() => {
                  if (!isSpeaking()) setSpeaking(false);
                });
              }
            }
          }}
        />
        {voiceStatus && (
          <div
            data-testid="theo-voice-status"
            style={{
              margin: "-6px 4px 4px", fontSize: 11.5, color: "#7C3AED",
              fontWeight: 500, letterSpacing: "-0.005em",
            }}
          >
            {voiceStatus}
          </div>
        )}

        {/* Starter chips — only when nothing is being said yet. They live
            INSIDE the dock so they stay reachable without scrolling. */}
        {!sending && !draftAssistant && (
          <div
            data-testid="theo-starter-chips"
            className="theo-h-scroll"
            style={{
              display: "flex", gap: 8, overflowX: "auto",
              marginTop: 6, paddingBottom: 2,
              scrollbarWidth: "none", msOverflowStyle: "none",
            }}
          >
            {buildStarters(ctx).map((s) => (
              <button
                key={s.id}
                data-testid={`theo-starter-${s.id}`}
                onClick={() => sendMessage(s.text)}
                style={{
                  flexShrink: 0,
                  background: "rgba(167, 139, 250, 0.06)",
                  color: "#5B21B6",
                  border: "1px solid rgba(167, 139, 250, 0.20)",
                  borderRadius: 999,
                  padding: "7px 14px",
                  fontSize: 12.5,
                  fontWeight: 500,
                  letterSpacing: "-0.005em",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "background 0.16s ease, transform 0.12s ease",
                }}
                onMouseDown={(e) => e.currentTarget.style.transform = "scale(0.96)"}
                onMouseUp={(e) => e.currentTarget.style.transform = "scale(1)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
              >
                {s.text}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── CHAT THREAD (when active) ─────────────────────────── */}
      {isChatActive && (
        <div data-testid="theo-thread" style={{
          background: "#fff", borderRadius: 22, padding: 14, marginBottom: 16,
          boxShadow: "0 1px 2px rgba(15,23,42,0.04)", border: "1px solid #F1F5F9",
        }}>
          {messages.map((m, i) => (
            <MessageBubble key={i} role={m.role} content={m.content} sources={m.sources} kind={m.kind} />
          ))}
          {draftAssistant && <MessageBubble role="assistant" content={draftAssistant} sources={activeSources(ctx)} streaming />}
          {/* Sentinel for auto-scroll. scroll-margin-bottom keeps the latest
              bubble visible ABOVE the fixed composer dock. */}
          <div ref={messagesEndRef} style={{ scrollMarginBottom: 200 }} />
        </div>
      )}

      {/* ── TODAY'S FOCUS + THEO KNOWS — hidden during Conversation Mode.
           Available on-demand via "View insights" in the fixed header. */}
      {!isChatActive && (
      <>
      {/* ── TODAY'S FOCUS — compact 2-item list ───────────────── */}
      <Card testid="theo-todays-focus" className="theo-fade">
        <SectionLabel>TODAY&apos;S FOCUS</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 6 }}>
          {focus.map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 14.5, color: "#0F172A", fontWeight: 500 }}>
              <CheckCircle />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* (Suggested Questions section replaced by chip rail below composer) */}

      {/* ── THEO MEMORY CENTER — remembered facts as bullets ──── */}
      <Card testid="theo-memory-center" className="theo-fade">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: conf.dot, boxShadow: `0 0 8px ${conf.dot}` }} />
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A", letterSpacing: "-0.008em" }}>
              Theo knows
            </div>
          </div>
          <button
            onClick={() => setUnderstandsOpen((o) => !o)}
            data-testid="theo-memory-toggle"
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              fontSize: 11.5, fontWeight: 600, color: "#7C3AED", letterSpacing: "0.04em",
              display: "flex", alignItems: "center", gap: 4,
            }}
          >
            {understandsOpen ? "Show less" : "Show more"} <span style={{ transition: "transform 0.22s", display: "inline-block", transform: understandsOpen ? "rotate(90deg)" : "none" }}>›</span>
          </button>
        </div>

        <ul data-testid="theo-knows-list" style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          {buildTheoKnows(ctx).slice(0, understandsOpen ? 7 : 4).map((item, i) => (
            <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13.5, color: "#1F2937", lineHeight: 1.5 }}>
              <span style={{
                flexShrink: 0, marginTop: 7,
                width: 5, height: 5, borderRadius: "50%",
                background: "linear-gradient(135deg, #A78BFA, #F4B6E8)",
                boxShadow: "0 0 6px rgba(167,139,250,0.6)",
              }} />
              <span style={{ fontWeight: 500 }}>{item}</span>
            </li>
          ))}
        </ul>

        {understandsOpen && (
          <div style={{ marginTop: 14, padding: 12, background: "#FAFAF9", borderRadius: 12, border: "1px solid #F1F5F9" }}>
            <p style={{ fontSize: 11.5, color: "#64748B", margin: 0, lineHeight: 1.6 }}>
              Theo remembers what you share — your cycle, symptoms, hormone reports, and goals. The more you check in, the more grounded these observations become.
            </p>
          </div>
        )}
      </Card>
      </>
      )}

      {error && (
        <div data-testid="theo-error" style={{
          background: "#FEE2E2", color: "#991B1B", padding: "10px 14px",
          borderRadius: 12, fontSize: 12, marginTop: 10, fontWeight: 500,
        }}>{error}</div>
      )}

      {!isChatActive && (
      <p style={{ fontSize: 10.5, color: "#94A3B8", textAlign: "center", margin: "18px 0 0", letterSpacing: "0.04em" }}>
        Educational support — not medical advice.
      </p>
      )}

      {/* ── INSIGHTS BOTTOM SHEET ───────────────────────────────────────
          Opens from "View insights" in the fixed header. Surfaces the
          same dashboard cards (Today's Brief, Theo Knows, focus) that
          we hide during Conversation Mode — without removing any data
          or capability. */}
      {insightsOpen && (
        <>
          <div
            onClick={() => setInsightsOpen(false)}
            data-testid="theo-insights-scrim"
            style={{
              position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.36)",
              zIndex: 110, animation: "theoInsightsFade 0.18s ease-out both",
            }}
          />
          <section
            data-testid="theo-insights-sheet"
            role="dialog"
            aria-label="Today's insights"
            style={{
              position: "fixed",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "100%",
              maxWidth: 420,
              maxHeight: "82vh",
              background: "#FAFAF9",
              borderTopLeftRadius: 26,
              borderTopRightRadius: 26,
              boxShadow: "0 -12px 48px rgba(15, 23, 42, 0.18)",
              zIndex: 120,
              padding: "10px 18px 28px",
              overflowY: "auto",
              animation: "theoInsightsSlide 0.28s cubic-bezier(0.16,1,0.3,1) both",
            }}
          >
            {/* Drag handle */}
            <div style={{
              width: 38, height: 4, borderRadius: 2,
              background: "#CBD5E1", margin: "8px auto 14px",
            }} />

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <h3 style={{ fontSize: 17, fontWeight: 800, color: "#0F172A", margin: 0, letterSpacing: "-0.012em" }}>
                Today&apos;s insights
              </h3>
              <button
                data-testid="theo-insights-close"
                onClick={() => setInsightsOpen(false)}
                aria-label="Close insights"
                style={{
                  background: "transparent", border: "none", padding: 4, cursor: "pointer",
                  color: "#64748B",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Today's Observation */}
            <div data-testid="theo-insights-today" style={{
              background: "#fff", borderRadius: 18, padding: "18px 18px 16px",
              boxShadow: "0 1px 2px rgba(15,23,42,0.04)", border: "1px solid #F1F5F9",
              marginBottom: 14,
            }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: "#7C3AED", letterSpacing: "0.14em", marginBottom: 8 }}>
                TODAY
              </div>
              <p style={{ fontSize: 15, color: "#0F172A", margin: "0 0 6px", lineHeight: 1.45, fontWeight: 600, letterSpacing: "-0.006em" }}>
                {obs.headline}
              </p>
              <p style={{ fontSize: 13, color: "#475569", margin: "0 0 12px", lineHeight: 1.55 }}>
                {obs.why}
              </p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <SourcePill label="Cycle" ok={obs.has.cycle} />
                <SourcePill label="Hormones" ok={obs.has.hormones} />
                <SourcePill label="Symptoms" ok={obs.has.symptoms} />
              </div>
            </div>

            {/* Today's Focus */}
            <Card testid="theo-insights-focus">
              <SectionLabel>TODAY&apos;S FOCUS</SectionLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
                {focus.map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#0F172A", fontWeight: 500 }}>
                    <CheckCircle />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Theo Knows */}
            <Card testid="theo-insights-knows">
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: conf.dot, boxShadow: `0 0 8px ${conf.dot}` }} />
                <div style={{ fontSize: 14.5, fontWeight: 700, color: "#0F172A", letterSpacing: "-0.008em" }}>
                  Theo knows
                </div>
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 9 }}>
                {buildTheoKnows(ctx).slice(0, 7).map((item, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: "#1F2937", lineHeight: 1.5 }}>
                    <span style={{
                      flexShrink: 0, marginTop: 6,
                      width: 5, height: 5, borderRadius: "50%",
                      background: "linear-gradient(135deg, #A78BFA, #F4B6E8)",
                      boxShadow: "0 0 6px rgba(167,139,250,0.6)",
                    }} />
                    <span style={{ fontWeight: 500 }}>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <p style={{ fontSize: 10.5, color: "#94A3B8", textAlign: "center", margin: "10px 0 0", letterSpacing: "0.04em" }}>
              Educational support — not medical advice.
            </p>
          </section>
        </>
      )}

      <style>{`
        .theo-fade { animation: theoFadeUp 0.55s cubic-bezier(0.16,1,0.3,1) both; }
        @keyframes theoFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .theo-h-scroll::-webkit-scrollbar { display: none; }
        @keyframes theoMicPulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(236,72,153,0.18), 0 4px 12px rgba(236,72,153,0.30); }
          50%      { box-shadow: 0 0 0 9px rgba(236,72,153,0.10), 0 4px 16px rgba(236,72,153,0.40); }
        }
        @keyframes theoSpeakerPulse {
          0%, 100% { background: rgba(124, 58, 237, 0.10); }
          50%      { background: rgba(124, 58, 237, 0.20); }
        }
        @keyframes theoInsightsFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes theoInsightsSlide {
          from { transform: translate(-50%, 100%); }
          to   { transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────

function Card({ children, testid, interactive = false, onClick, className = "" }) {
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className={className}
      style={{
        background: "#fff",
        borderRadius: 20,
        padding: 18,
        marginBottom: 14,
        boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.04)",
        border: "1px solid #F1F5F9",
        cursor: interactive ? "pointer" : "default",
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
      }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children, style }) {
  return (
    <div style={{
      fontSize: 10.5, fontWeight: 800, color: "#7C3AED",
      letterSpacing: "0.14em", marginBottom: 10, ...style,
    }}>
      {children}
    </div>
  );
}

function SourcePill({ label, ok }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: 11.5, fontWeight: 600,
      color: ok ? "#059669" : "#94A3B8",
    }}>
      <span style={{ fontSize: 13, lineHeight: 1, fontWeight: 800 }}>{ok ? "✓" : "○"}</span>
      {label}
    </span>
  );
}

function CheckCircle() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill="#F5F3FF" />
      <path d="M8 12.5l2.5 2.5L16 9.5" stroke="#7C3AED" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Composer({ input, setInput, onSend, sending, hasData, listening, voiceMode, speaking, onVoiceToggle, onVoiceModeToggle }) {
  const speakerActive = voiceMode || speaking;
  return (
    <div
      data-testid="theo-composer"
      style={{
        position: "relative",
        background: "#fff",
        borderRadius: 999,
        padding: 6,
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginBottom: 16,
        boxShadow: "0 6px 20px rgba(15,23,42,0.06)",
        border: listening ? "1px solid #7C3AED" : "1px solid #F1F5F9",
        transition: "border-color 0.2s ease",
      }}
    >
      {/* Speaker / voice-replies toggle. Tap to play current reply, tap again
          to stop. While speaking, the icon shows a pink-purple "active" look. */}
      <button
        data-testid="theo-voicemode-toggle"
        onClick={onVoiceModeToggle}
        disabled={sending}
        aria-label={speaking ? "Stop voice playback" : (voiceMode ? "Voice replies on — tap to stop" : "Play voice reply")}
        title={speaking ? "Tap to stop" : (voiceMode ? "Voice replies on — tap to stop" : "Tap to hear Theo speak")}
        style={{
          background: speakerActive ? "rgba(124, 58, 237, 0.10)" : "transparent",
          color: speakerActive ? "#7C3AED" : "#94A3B8",
          border: "none", borderRadius: "50%", width: 36, height: 36, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: sending ? "not-allowed" : "pointer",
          transition: "background 0.15s ease, color 0.15s ease",
          marginLeft: 2,
          animation: speaking ? "theoSpeakerPulse 1.6s ease-in-out infinite" : "none",
        }}
      >
        {speakerActive ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
          </svg>
        )}
      </button>
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !sending) onSend(input); }}
        placeholder={
          listening ? "Listening…" :
          (sending ? "Theo is thinking…" : (hasData ? "Talk to Theo…" : "Tell Theo anything — even hello…"))
        }
        disabled={sending}
        data-testid="theo-input"
        style={{
          flex: 1, border: "none", outline: "none",
          padding: "12px 12px", fontSize: 14.5,
          background: "transparent", color: "#0F172A",
          minWidth: 0,
        }}
      />
      {/* Mic button — tap-to-talk */}
      <button
        data-testid="theo-mic-btn"
        onClick={onVoiceToggle}
        disabled={sending}
        aria-label={listening ? "Stop listening" : "Talk to Theo"}
        title={listening ? "Tap to stop" : "Tap to talk"}
        style={{
          background: listening
            ? "linear-gradient(135deg, #F472B6, #EC4899)"
            : (input.trim() ? "transparent" : "linear-gradient(135deg, #A78BFA, #7C3AED)"),
          color: listening ? "#fff" : (input.trim() ? "#7C3AED" : "#fff"),
          border: "none", borderRadius: "50%",
          width: 40, height: 40, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: sending ? "not-allowed" : "pointer",
          boxShadow: listening
            ? "0 0 0 4px rgba(236,72,153,0.18), 0 4px 12px rgba(236,72,153,0.30)"
            : (input.trim() ? "none" : "0 4px 12px rgba(124,58,237,0.30)"),
          transition: "all 0.18s ease",
          animation: listening ? "theoMicPulse 1.4s ease-in-out infinite" : "none",
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="2" width="6" height="12" rx="3" ry="3" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
      </button>
      {/* Send button (text-mode) */}
      <button
        onClick={() => onSend(input)}
        disabled={!input.trim() || sending}
        data-testid="theo-send-btn"
        aria-label="Send"
        style={{
          background: input.trim() && !sending
            ? "linear-gradient(135deg, #A78BFA, #7C3AED)"
            : "#E5E7EB",
          color: "#fff", border: "none", borderRadius: "50%",
          width: 40, height: 40,
          display: input.trim() ? "flex" : "none",
          alignItems: "center", justifyContent: "center",
          cursor: input.trim() && !sending ? "pointer" : "not-allowed",
          transition: "transform 0.15s ease",
          boxShadow: input.trim() && !sending ? "0 4px 12px rgba(124,58,237,0.30)" : "none",
        }}
        onMouseDown={(e) => e.currentTarget.style.transform = "scale(0.92)"}
        onMouseUp={(e) => e.currentTarget.style.transform = "scale(1)"}
        onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="22" y1="2" x2="11" y2="13" />
          <polygon points="22 2 15 22 11 13 2 9 22 2" />
        </svg>
      </button>
    </div>
  );
}

function MessageBubble({ role, content, sources, kind, streaming = false }) {
  const isUser = role === "user";
  const isDailyBrief = !isUser && kind === "daily_brief";
  const showSources = !isUser && Array.isArray(sources) && sources.length > 0;
  return (
    <div style={{
      display: "flex", flexDirection: "column",
      alignItems: isUser ? "flex-end" : "flex-start",
      marginBottom: 16, animation: "theoFadeUp 0.35s cubic-bezier(0.16,1,0.3,1) both",
    }}>
      {/* Daily Brief label — subtle premium accent above today's greeting */}
      {isDailyBrief && (
        <div
          data-testid="theo-daily-brief-label"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 6,
            paddingLeft: 2,
          }}
        >
          <span style={{
            width: 6, height: 6, borderRadius: "50%",
            background: "linear-gradient(135deg, #FDBA74, #FB7185, #C4B5FD)",
            boxShadow: "0 0 8px rgba(251, 113, 133, 0.65)",
          }} />
          <span style={{
            fontSize: 9.5, fontWeight: 800, letterSpacing: "0.16em",
            color: "#7C3AED", textTransform: "uppercase",
          }}>
            Today&apos;s Brief
          </span>
        </div>
      )}
      {/* Transparency chips — only above Theo's responses */}
      {showSources && (
        <div data-testid="theo-msg-sources" style={{
          display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap",
        }}>
          <span style={{
            fontSize: 9.5, fontWeight: 700, color: "#94A3B8",
            letterSpacing: "0.14em", textTransform: "uppercase",
          }}>
            Based on
          </span>
          {sources.slice(0, 4).map((s) => (
            <span key={s} style={{
              fontSize: 10, fontWeight: 600,
              color: "#6D28D9",
              background: "rgba(167, 139, 250, 0.10)",
              border: "1px solid rgba(167, 139, 250, 0.22)",
              padding: "2px 8px", borderRadius: 999,
              letterSpacing: "0.02em",
              display: "inline-flex", alignItems: "center", gap: 4,
            }}>
              <span style={{
                width: 4, height: 4, borderRadius: "50%",
                background: "linear-gradient(135deg, #A78BFA, #F4B6E8)",
              }} />
              {s}
            </span>
          ))}
        </div>
      )}
      <div style={{
        maxWidth: "85%",
        background: isUser
          ? "linear-gradient(135deg, #A78BFA, #7C3AED)"
          : "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(250,250,255,0.95))",
        backdropFilter: isUser ? "none" : "blur(8px)",
        WebkitBackdropFilter: isUser ? "none" : "blur(8px)",
        color: isUser ? "#fff" : "#0F172A",
        padding: isUser ? "12px 18px" : "14px 18px",
        borderRadius: isUser ? "22px 22px 8px 22px" : "22px 22px 22px 8px",
        fontSize: 14.5, lineHeight: 1.65,
        whiteSpace: "pre-wrap", wordBreak: "break-word",
        boxShadow: isUser
          ? "0 6px 18px rgba(124,58,237,0.22), inset 0 1px 0 rgba(255,255,255,0.18)"
          : (isDailyBrief
              ? "0 4px 14px rgba(251, 113, 133, 0.10), 0 12px 30px rgba(167, 139, 250, 0.10)"
              : "0 2px 6px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.05)"),
        border: isUser
          ? "none"
          : (isDailyBrief
              ? "1px solid rgba(244, 182, 232, 0.55)"
              : "1px solid rgba(241, 245, 249, 0.9)"),
        letterSpacing: isUser ? 0 : "-0.005em",
      }}>
        {content}
        {streaming && <span style={{ opacity: 0.55, marginLeft: 2 }}>▍</span>}
      </div>
    </div>
  );
}
